import { router } from "@inertiajs/react"
import { ChevronRight, Sparkles } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { crewMembershipPath } from "@/routes"
import type {
  CrewAnalyticsWindow,
  TopPerformerBand,
  TopPerformerRow,
} from "@/types/crew-analytics"

/**
 * OKS-65 — Top-5 performer crew members.
 *
 * Ranked by `reliability_score DESC` (window-driven via OKS-64's
 * `ReliabilityScoreBatch`). N/A members excluded server-side. Click
 * anywhere on a row → `crewMembershipPath(id)`.
 *
 * Traffic-light `Badge` reads the locked band discriminator:
 *   - excellent → `default` variant (brand-tinted)
 *   - good      → `secondary` variant
 *   - at_risk   → `destructive` variant (honest red on a low-reliability brand)
 *
 * Empty array → empty card ("Not enough collab history yet — top
 * performers appear once members complete ≥3 collabs each.")
 */
interface CrewTopPerformersTableProps {
  rows: TopPerformerRow[]
  windowLabel: CrewAnalyticsWindow
}

const BAND_VARIANT: Record<
  TopPerformerBand,
  "default" | "secondary" | "destructive"
> = {
  excellent: "default",
  good: "secondary",
  at_risk: "destructive",
}

export function CrewTopPerformersTable({
  rows,
  windowLabel,
}: CrewTopPerformersTableProps) {
  const { t } = useTranslation()

  if (rows.length === 0) {
    return (
      <Card data-testid="crew-analytics-top-performers-empty">
        <CardContent className="flex flex-col items-center gap-3 p-8 text-center">
          <Sparkles
            className="text-muted-foreground h-8 w-8"
            aria-hidden="true"
          />
          <p className="text-muted-foreground text-sm">
            {t("analytics.crew.top_performers.empty")}
          </p>
        </CardContent>
      </Card>
    )
  }

  const navigateToMember = (id: number) => {
    router.visit(crewMembershipPath(id))
  }

  return (
    <Card data-testid="crew-top-performers-table">
      <CardHeader>
        <CardTitle>{t("analytics.crew.top_performers.title")}</CardTitle>
        <p className="text-muted-foreground text-sm">
          {t("analytics.crew.top_performers.subtitle", {
            window: t(`analytics.crew.window.${windowLabel}`),
          })}
        </p>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                {t("analytics.crew.top_performers.columns.creator")}
              </TableHead>
              <TableHead className="text-right">
                {t("analytics.crew.top_performers.columns.score")}
              </TableHead>
              <TableHead>
                {t("analytics.crew.top_performers.columns.band")}
              </TableHead>
              <TableHead className="text-right">
                {t("analytics.crew.top_performers.columns.submissions")}
              </TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.crew_membership_id}
                className="hover:bg-muted/50 cursor-pointer"
                onClick={() => navigateToMember(row.crew_membership_id)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault()
                    navigateToMember(row.crew_membership_id)
                  }
                }}
                tabIndex={0}
                role="link"
                data-testid={`crew-top-performer-row-${row.crew_membership_id}`}
              >
                <TableCell className="font-medium">
                  {row.tiktok_username ??
                    t("analytics.crew.top_performers.handle_missing")}
                </TableCell>
                <TableCell className="text-right text-lg font-semibold tabular-nums">
                  {row.reliability_score}
                </TableCell>
                <TableCell>
                  <Badge variant={BAND_VARIANT[row.reliability_band]}>
                    {t(
                      `analytics.crew.top_performers.bands.${row.reliability_band}`,
                    )}
                  </Badge>
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {row.in_window_submissions}
                </TableCell>
                <TableCell>
                  <ChevronRight
                    className="text-muted-foreground h-4 w-4"
                    aria-hidden="true"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}
